import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthShell, Field } from "@/pages/Login";

const inputCls = "w-full bg-navy border border-white/20 text-white p-3 focus:border-gold focus:outline-none";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const welcome = params.get("welcome") === "1";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success(welcome ? "Welcome! You're all set — please sign in." : "Password reset — please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid reset link" subtitle="This password reset link is missing or malformed.">
        <p className="text-gray-400 text-center mb-8" data-testid="reset-invalid">Please request a new reset link.</p>
        <Link to="/forgot-password" className="block text-center text-gold hover:underline" data-testid="request-new">Request a new link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={welcome ? "Set up your portal" : "Choose a new password"} subtitle={welcome ? "Create a password to open your client portal." : "Enter and confirm your new password below."}>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        {error && <div className="bg-destructive/15 border border-destructive/40 text-red-300 text-sm px-4 py-3" data-testid="reset-error">{error}</div>}
        <Field label="New password">
          <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
            data-testid="reset-password" className={inputCls} placeholder="At least 8 characters" />
        </Field>
        <Field label="Confirm password">
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm" className={inputCls} placeholder="••••••••" />
        </Field>
        <button type="submit" disabled={loading} data-testid="reset-submit"
          className="w-full bg-gold text-navy py-4 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 className="animate-spin" size={18} />} {welcome ? "Set password & open portal" : "Reset password"}
        </button>
      </form>
    </AuthShell>
  );
}
